import React, { useRef, useState, useEffect, memo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { logoutAction } from "../../redux/actions/authActions";
import "./Navbar.css";

import {
  HiOutlineMoon,
  HiOutlineSun,
  HiOutlineOfficeBuilding,
  HiOutlineMenuAlt2,
  HiOutlineX,
  HiOutlineSearch,
  HiOutlineChevronDown,
  HiOutlineBell,
  HiOutlineShieldCheck,
} from "react-icons/hi";
import { IoLogOutOutline } from "react-icons/io5";

import BrandLogo from "./BrandLogo";
import CampaignBuilderModal from "../modals/CampaignBuilderModal";

const MOCK_NOTIFICATIONS = [];

const Navbar = ({ userData }) => {
  const dispatch = useDispatch();
  const [loggingOut, setLoggingOut] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCampaignBuilder, setShowCampaignBuilder] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);

  const currentOrganisation = useSelector(
    (state) => state.organisation?.currentOrganisation
  );
  const unreadNotificationsCount = notifications.filter((n) => !n.read).length;

  const profileDropdownRef = useRef(null);
  const notificationsDropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const savedDarkMode = localStorage.getItem("darkMode");
    if (savedDarkMode !== null) setDarkMode(savedDarkMode === "true");
  }, []);

  useEffect(() => {
    if (darkMode) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === "Escape" && mobileMenuOpen) setMobileMenuOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [mobileMenuOpen]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        profileDropdownRef.current &&
        !profileDropdownRef.current.contains(event.target)
      )
        setShowProfileDropdown(false);
      if (
        notificationsDropdownRef.current &&
        !notificationsDropdownRef.current.contains(event.target)
      )
        setShowNotifications(false);
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const toggleProfileDropdown = () => {
    setShowProfileDropdown(!showProfileDropdown);
    if (showNotifications) setShowNotifications(false);
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    if (showProfileDropdown) setShowProfileDropdown(false);
  };

  const markAllNotificationsAsRead = () => {
    setNotifications(notifications.map((n) => ({ ...n, read: true })));
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    console.log("Searching for:", searchQuery);
  };

  const logout = async () => {
    setLoggingOut(true);
    await dispatch(logoutAction());
    setLoggingOut(false);
  };

  const userInitial = userData?.name ? userData.name.charAt(0).toUpperCase() : "U";

  return (
    <nav className="navbar sticky top-0 z-30 h-16 bg-canvas border-b border-hairline">
      {showCampaignBuilder && (
        <CampaignBuilderModal closeModal={() => setShowCampaignBuilder(false)} />
      )}

      <div className="w-full md:w-10/12 mx-auto h-full px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-full gap-4">
          {/* Left: hamburger + logo */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="nav-icon-button inline-flex items-center justify-center w-10 h-10 rounded-md text-muted lg:hidden"
              aria-controls="mobile-menu"
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-help-title="Menu"
              data-help="Opens the mobile menu with a search field, a link to account security, and a sign out option. Only shown on smaller screens."
            >
              <span className="sr-only">
                {mobileMenuOpen ? "Close menu" : "Open menu"}
              </span>
              {mobileMenuOpen ? (
                <HiOutlineX className="h-5 w-5" aria-hidden="true" />
              ) : (
                <HiOutlineMenuAlt2 className="h-5 w-5" aria-hidden="true" />
              )}
            </button>

            <Link
              to="/"
              className="navbar-logo flex items-center"
              aria-label="LoyalBase home"
              data-help-title="Home"
              data-help="Returns you to the home page, which opens Campaigns."
            >
              <BrandLogo className="h-7 lg:h-9 xl:h-10 w-auto" alt="" />
            </Link>
          </div>

          {/* Center: search */}
          <div className="hidden lg:flex flex-1 max-w-md">
            <form onSubmit={handleSearchSubmit} className="relative w-full">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <HiOutlineSearch className="h-4 w-4 text-muted" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="navbar-search block w-full h-10 pl-10 pr-12 text-body-md text-ink bg-canvas border border-hairline rounded-sm focus:outline-none focus:border-info-border placeholder:text-muted"
                placeholder="Search..."
                data-help-title="Global search"
                data-help="Lets you type a search query from anywhere in the app. Press Ctrl+K or Cmd+K to jump to this field. Full search results are not available yet."
              />
              <div className="search-shortcut absolute right-3 top-1/2 -translate-y-1/2 text-[11px] py-0.5 px-1.5 rounded-xs text-muted bg-surface-soft border border-hairline">
                ⌘K
              </div>
            </form>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-1 sm:gap-2">
            <button
              onClick={toggleDarkMode}
              className="nav-icon-button inline-flex items-center justify-center w-10 h-10 rounded-md text-muted"
              aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
              data-help-title="Dark mode"
              data-help="Switches between light and dark themes. Your preference is remembered for future visits."
            >
              {darkMode ? (
                <HiOutlineSun className="h-5 w-5" />
              ) : (
                <HiOutlineMoon className="h-5 w-5" />
              )}
            </button>

            <div className="relative" ref={notificationsDropdownRef}>
              <button
                type="button"
                className="nav-icon-button inline-flex items-center justify-center w-10 h-10 rounded-md text-muted relative"
                onClick={toggleNotifications}
                aria-label={`${unreadNotificationsCount} unread notifications`}
                aria-haspopup="true"
                aria-expanded={showNotifications}
                data-help-title="Notifications"
                data-help="Opens your notifications. A coral dot on the bell means there are unread items."
              >
                <HiOutlineBell className="h-5 w-5" />
                {unreadNotificationsCount > 0 && (
                  <span className="notification-badge bg-signature-coral h-2 w-2 rounded-full ring-2 ring-canvas" />
                )}
              </button>

              <div
                className={`notification-dropdown absolute right-0 mt-2 w-80 rounded-md bg-canvas border border-hairline shadow-card z-50 overflow-hidden ${
                  showNotifications ? "show" : ""
                }`}
              >
                <div className="px-4 py-3 border-b border-hairline flex justify-between items-center">
                  <h3 className="text-title-sm text-ink">Notifications</h3>
                  <button
                    onClick={markAllNotificationsAsRead}
                    className="text-body-md text-link hover:text-link-active"
                    data-help-title="Mark all read"
                    data-help="Marks every notification as read and removes the unread dot from the bell."
                  >
                    Mark all as read
                  </button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-6 text-center text-body-md text-muted">
                      No notifications
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`notification-item p-4 border-b border-hairline flex items-start gap-3 ${
                          notification.read ? "bg-canvas" : "bg-surface-soft"
                        }`}
                      >
                        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-surface-soft border border-hairline text-ink">
                          <HiOutlineBell className="h-4 w-4" />
                        </div>
                        <div>
                          <p className="text-body-md font-medium text-ink">
                            {notification.title}
                          </p>
                          <p className="text-[12px] mt-1 text-muted">
                            {notification.time}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-3 text-center border-t border-hairline">
                  <Link
                    to="/notifications"
                    className="text-body-md text-link hover:text-link-active"
                  >
                    View all notifications
                  </Link>
                </div>
              </div>
            </div>

            <div className="organisation-selector hidden sm:flex items-center gap-1.5 px-3 h-10 rounded-md bg-surface-soft text-ink border border-hairline cursor-pointer" data-help-title="Organisation" data-help="Shows the organisation you are currently working in.">
              <HiOutlineOfficeBuilding className="h-4 w-4 text-muted" />
              <span className="text-body-md font-medium truncate max-w-[120px]">
                {currentOrganisation?.name || "Select Organization"}
              </span>
              <HiOutlineChevronDown className="h-3 w-3 text-muted" />
            </div>

            <div className="relative" ref={profileDropdownRef}>
              <button
                onClick={toggleProfileDropdown}
                className="flex items-center"
                aria-expanded={showProfileDropdown}
                aria-haspopup="true"
                data-help-title="Account menu"
                data-help="Opens your account menu showing your name and email, with a link to your account security settings and the option to sign out."
              >
                <div className="avatar-container h-9 w-9 rounded-full overflow-hidden border border-hairline bg-surface-soft">
                  {userData?.profilePicture ? (
                    <img
                      src={userData.profilePicture}
                      alt={userData.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center font-medium text-ink">
                      {userInitial}
                    </div>
                  )}
                </div>
              </button>

              <div
                className={`profile-dropdown absolute right-0 mt-2 w-64 rounded-md bg-canvas border border-hairline shadow-card z-50 overflow-hidden ${
                  showProfileDropdown ? "show" : ""
                }`}
              >
                <div className="p-4 border-b border-hairline flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full overflow-hidden border border-hairline bg-surface-soft flex-shrink-0">
                    {userData?.profilePicture ? (
                      <img
                        src={userData.profilePicture}
                        alt={userData.name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center font-medium text-ink">
                        {userInitial}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-body-md font-medium text-ink truncate">
                      {userData?.name || ""}
                    </p>
                    <p className="text-[12px] text-muted truncate">
                      {userData?.email || "email@example.com"}
                    </p>
                  </div>
                </div>

                <div className="py-1">
                  <Link
                    to="/account/security"
                    onClick={() => setShowProfileDropdown(false)}
                    className="notification-item flex items-center gap-2 w-full text-left px-4 py-2.5 text-body-md text-ink"
                    data-help-title="Account security"
                    data-help="Opens your personal security settings, where you can turn on two-factor authentication and manage backup codes."
                  >
                    <HiOutlineShieldCheck className="h-4 w-4" />
                    <span>Account security</span>
                  </Link>
                  <button
                    onClick={logout}
                    disabled={loggingOut}
                    className="notification-item flex items-center gap-2 w-full text-left px-4 py-2.5 text-body-md text-signature-coral disabled:opacity-60"
                    data-help-title="Sign out"
                    data-help="Signs you out of your LoyalBase account on this device."
                  >
                    <IoLogOutOutline className="h-4 w-4" />
                    <span>{loggingOut ? "Logging out..." : "Sign out"}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sheet */}
      <div
        className={`mobile-menu fixed inset-0 z-40 lg:hidden ${
          mobileMenuOpen ? "open" : ""
        }`}
        style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
      >
        <div className="fixed inset-y-0 left-0 w-full max-w-xs overflow-y-auto bg-canvas border-r border-hairline">
          <div className="px-4 pt-5 pb-6">
            <div className="flex items-center justify-between mb-6">
              <Link
                to="/"
                className="navbar-logo flex items-center"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="LoyalBase home"
              >
                <BrandLogo className="h-8 w-auto" alt="" />
              </Link>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="nav-icon-button inline-flex items-center justify-center w-10 h-10 rounded-md text-muted"
                aria-label="Close menu"
              >
                <HiOutlineX className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSearchSubmit} className="relative mb-6">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <HiOutlineSearch className="h-4 w-4 text-muted" />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="navbar-search block w-full h-11 pl-10 pr-3 text-body-md text-ink bg-surface-soft border border-hairline rounded-sm focus:outline-none focus:border-info-border placeholder:text-muted"
                placeholder="Search..."
              />
            </form>

            <div className="pt-6 border-t border-hairline flex flex-col gap-3">
              <Link
                to="/account/security"
                onClick={() => setMobileMenuOpen(false)}
                className="w-full inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg border border-hairline text-body-md font-medium text-ink"
                data-help-title="Account security"
                data-help="Opens your personal security settings, where you can turn on two-factor authentication and manage backup codes."
              >
                <HiOutlineShieldCheck className="h-5 w-5" />
                Account security
              </Link>
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  logout();
                }}
                disabled={loggingOut}
                className="w-full inline-flex items-center justify-center gap-2 h-11 px-4 rounded-lg border border-hairline text-body-md font-medium text-signature-coral disabled:opacity-60"
              >
                <IoLogOutOutline className="h-5 w-5" />
                {loggingOut ? "Logging out..." : "Sign out"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default memo(Navbar);
